import React from 'react';
import { useToastContext } from '../../context/ToastContext';

const TYPE_STYLES = {
  success: { borderColor: '#10B981', icon: '✓' },
  error:   { borderColor: '#E63946', icon: '✕' },
  info:    { borderColor: '#60A5FA', icon: 'i' },
};

function ToastItem({ id, message, type, onRemove }) {
  const { borderColor, icon } = TYPE_STYLES[type] || TYPE_STYLES.info;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        background: '#17130E',
        color: 'rgba(250,247,242,0.95)',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.10)',
        borderLeft: `4px solid ${borderColor}`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        padding: '12px 14px',
        minWidth: 260,
        maxWidth: 360,
        pointerEvents: 'auto',
        animation: 'vb-toast-in 0.25s cubic-bezier(0.16,1,0.3,1) both',
      }}
    >
      <span style={{
        flexShrink: 0,
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: borderColor,
        color: '#fff',
        fontSize: 11,
        fontWeight: 900,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 1,
      }}>
        {icon}
      </span>
      <span style={{ flex: 1, fontSize: 13, fontWeight: 500, lineHeight: 1.5 }}>{message}</span>
      <button
        onClick={() => onRemove(id)}
        style={{
          flexShrink: 0,
          background: 'none',
          border: 'none',
          color: 'rgba(250,247,242,0.70)',
          cursor: 'pointer',
          fontSize: 16,
          lineHeight: 1,
          padding: 0,
          marginTop: 1,
        }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

export function ToastContainer() {
  const { toasts, removeToast } = useToastContext();

  return (
    <>
      <style>{`
        @keyframes vb-toast-in {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          pointerEvents: 'none',
        }}
        aria-live="polite"
        aria-label="Notifications"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} {...t} onRemove={removeToast} />
        ))}
      </div>
    </>
  );
}

/**
 * useToast() returns { toast } with methods:
 *   toast.success(msg), toast.error(msg), toast.info(msg)
 */
export function useToast() {
  const { addToast } = useToastContext();
  const toast = React.useMemo(() => ({
    success: (msg) => addToast(msg, 'success'),
    error:   (msg) => addToast(msg, 'error'),
    info:    (msg) => addToast(msg, 'info'),
  }), [addToast]);
  return { toast };
}
